import { useMemo, useState } from "react";
import useSWR from "swr";
import { KeyRoundIcon, Loader2Icon, PencilIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { txToast } from "@/components/TxToaster";
import { useBackendApi } from "@/hooks/fetch";
import { useAdminPerms } from "@/hooks/auth";
import { useOpenConfirmDialog } from "@/hooks/dialogs";
import { tsToLocaleDateTimeString } from "@/lib/dateTime";
import { cn } from "@/lib/utils";
import type { ApiKeyListResp, ApiKeyPublicRecord, ApiKeyRevokeResp } from "@shared/apiV1Types";
import type { ApiScopeInfo } from "@shared/apiScopes";
import ApiKeyCreateDialog from "./ApiKeyCreateDialog";
import ApiKeyScopesDialog from "./ApiKeyScopesDialog";
import ApiKeyTokenDialog from "./ApiKeyTokenDialog";
import WebhooksSection from "./WebhooksSection";


type KeyStatus = 'active' | 'expired' | 'revoked';
const getKeyStatus = (key: ApiKeyPublicRecord, now: number): KeyStatus => {
    if (key.revokedAt) return 'revoked';
    if (key.expiresAt && key.expiresAt <= now) return 'expired';
    return 'active';
};
const statusBadgeClass: Record<KeyStatus, string> = {
    active: 'bg-success/15 text-success border-success/30',
    expired: 'bg-warning/15 text-warning border-warning/30',
    revoked: 'bg-destructive/15 text-destructive border-destructive/30',
};


function ApiKeyRow({ apiKey, onEditScopes, onRevoke, canManage, scopes }: {
    apiKey: ApiKeyPublicRecord;
    onEditScopes: (key: ApiKeyPublicRecord) => void;
    onRevoke: (key: ApiKeyPublicRecord) => void;
    canManage: boolean;
    scopes: ApiScopeInfo[];
}) {
    const status = getKeyStatus(apiKey, Date.now());
    return (
        <TableRow className={cn(status !== 'active' && 'opacity-60')}>
            <TableCell>
                <div className="font-semibold">{apiKey.name}</div>
                <div className="font-mono text-xs text-muted-foreground">txk_{apiKey.id}.&hellip;</div>
            </TableCell>
            <TableCell>
                <Badge variant="outline" className={statusBadgeClass[status]}>{status}</Badge>
            </TableCell>
            <TableCell className="max-w-[18rem]">
                <div className="flex flex-wrap gap-1">
                    {apiKey.permissions.length === 0 && (
                        <Badge variant="outline" className="text-xs font-normal">Read-only</Badge>
                    )}
                    {apiKey.permissions.map((scopeId) => (
                        <Badge key={scopeId} variant="secondary" className="text-xs font-normal" title={scopeId}>
                            {scopes.find((s) => s.id === scopeId)?.label ?? scopeId}
                        </Badge>
                    ))}
                </div>
            </TableCell>
            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                <div>{tsToLocaleDateTimeString(apiKey.createdAt, 'medium', 'short')}</div>
                <div>by {apiKey.createdBy}</div>
            </TableCell>
            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                {apiKey.lastUsedAt ? tsToLocaleDateTimeString(apiKey.lastUsedAt, 'medium', 'short') : 'never'}
            </TableCell>
            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                {apiKey.expiresAt ? tsToLocaleDateTimeString(apiKey.expiresAt, 'medium', 'short') : 'never'}
                {apiKey.allowedIps.length ? (
                    <div title={apiKey.allowedIps.join(', ')}>{apiKey.allowedIps.length} IP rule(s)</div>
                ) : null}
            </TableCell>
            <TableCell className="text-right whitespace-nowrap">
                {status !== 'revoked' && (
                    <Button
                        size="sm"
                        variant="ghost"
                        disabled={!canManage}
                        onClick={() => onEditScopes(apiKey)}
                        title="Edit Scopes"
                    >
                        <PencilIcon className="size-4" />
                    </Button>
                )}
                {status !== 'revoked' && (
                    <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        disabled={!canManage}
                        onClick={() => onRevoke(apiKey)}
                        title="Revoke this key"
                    >
                        <Trash2Icon className="size-4" />
                    </Button>
                )}
            </TableCell>
        </TableRow>
    );
}


export default function ApiKeysPage() {
    const { hasPerm } = useAdminPerms();
    const canManage = hasPerm('manage.admins');
    const openConfirmDialog = useOpenConfirmDialog();
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [newToken, setNewToken] = useState<{ name: string; token: string } | null>(null);
    const [editingKey, setEditingKey] = useState<ApiKeyPublicRecord | null>(null);

    const listApi = useBackendApi<ApiKeyListResp>({
        method: 'GET',
        path: '/apiKeys',
    });
    const revokeApi = useBackendApi<ApiKeyRevokeResp, { id: string }>({
        method: 'POST',
        path: '/apiKeys/revoke',
    });

    const swr = useSWR('/apiKeys', async () => {
        const resp = await listApi({});
        if (!resp) throw new Error('No data returned');
        if ('error' in resp) throw new Error(resp.error.message);
        return resp.data;
    });

    const sortedKeys = useMemo(() => {
        if (!swr.data) return [];
        const now = Date.now();
        //active first, then expired, then revoked; newest first within each
        const rank: Record<KeyStatus, number> = { active: 0, expired: 1, revoked: 2 };
        return [...swr.data.keys].sort((a, b) => {
            const diff = rank[getKeyStatus(a, now)] - rank[getKeyStatus(b, now)];
            return diff !== 0 ? diff : b.createdAt - a.createdAt;
        });
    }, [swr.data]);

    const handleRevoke = (key: ApiKeyPublicRecord) => {
        openConfirmDialog({
            title: `Revoke API key "${key.name}"?`,
            message: 'Any integration using this key will stop working immediately. This cannot be undone.',
            actionLabel: 'Revoke',
            confirmBtnVariant: 'destructive',
            onConfirm: async () => {
                const resp = await revokeApi({
                    data: { id: key.id },
                    toastLoadingMessage: 'Revoking key...',
                });
                if (!resp) return;
                if ('error' in resp) {
                    txToast.error({ title: 'Failed to revoke key', msg: resp.error.message });
                } else {
                    txToast.success('API key revoked.');
                    swr.mutate();
                }
            },
        });
    };

    const handleScopesSaved = () => {
        setEditingKey(null);
        txToast.success('API key scopes updated.');
        swr.mutate();
    };

    const handleCreated = (name: string, token: string) => {
        setIsCreateOpen(false);
        setNewToken({ name, token });
        swr.mutate();
    };

    return (
        <div className="flex flex-col w-full gap-4 pb-4 [&>header]:mb-0">
            <PageHeader title="API Keys" icon={<KeyRoundIcon />}>
                <Button size="sm" disabled={!canManage || !swr.data} onClick={() => setIsCreateOpen(true)}>
                    <PlusIcon className="size-4 mr-1" /> New Key
                </Button>
            </PageHeader>


            {swr.error ? (
                <Alert variant="destructive" className="mx-2 md:mx-0 w-auto">
                    <AlertTitle>Failed to Load API Keys</AlertTitle>
                    <AlertDescription>{(swr.error as Error).message}</AlertDescription>
                </Alert>
            ) : !swr.data ? (
                <div className="flex items-center justify-center py-16 text-muted-foreground">
                    <Loader2Icon className="animate-spin size-6 mr-2" /> Loading...
                </div>
            ) : !sortedKeys.length ? (
                <div className="text-center py-16 text-muted-foreground border rounded-lg mx-2 md:mx-0 bg-card">
                    No API keys yet. {canManage ? 'Create one to get started.' : 'Ask an admin with the Manage Admins permission to create one.'}
                </div>
            ) : (
                <div className="border rounded-lg mx-2 md:mx-0 bg-card overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Key</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Scopes</TableHead>
                                <TableHead>Created</TableHead>
                                <TableHead>Last Used</TableHead>
                                <TableHead>Expires</TableHead>
                                <TableHead></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {sortedKeys.map((key) => (
                                <ApiKeyRow key={key.id} apiKey={key} onEditScopes={setEditingKey} onRevoke={handleRevoke} canManage={canManage} scopes={swr.data.scopes} />
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {swr.data && (
                <ApiKeyCreateDialog
                    isOpen={isCreateOpen}
                    onClose={() => setIsCreateOpen(false)}
                    onCreated={handleCreated}
                    scopes={swr.data.scopes}
                />
            )}
            {swr.data && editingKey && (
                <ApiKeyScopesDialog
                    key={editingKey.id}
                    apiKey={editingKey}
                    scopes={swr.data.scopes}
                    onClose={() => setEditingKey(null)}
                    onSaved={handleScopesSaved}
                />
            )}
            <ApiKeyTokenDialog
                data={newToken}
                onClose={() => setNewToken(null)}
            />

            <WebhooksSection canManage={canManage} />
        </div>
    );
}
